import * as React from 'react';
import { SpsIcon, TagKind } from '@spscommerce/ds-shared';
import { SpsSideNavBranch } from './SpsSideNavBranch';
import { useRef, useState } from "react";
import clsx from 'clsx';

export interface SideNavItems {
  name: string;
  tag?: TagKind;
  tagContent?: string;
  subNavItems?: SideNavItems[];
}

export interface SideNavSection {
  sectionTitle: string;
  navItems: SideNavItems[];
}

export interface SideNavBranch extends SideNavItems {
  expanded: boolean;
}

export interface SpsSideNavProps {
  title: string;
  titleIcon?: SpsIcon;
  activeNavItem: string;
  onNavChange?: (newNav: string) => void;
  navOptions: SideNavSection[];
  tabs?: string[];
  initialActiveTab?: string;
  onTabChange?: (newTab: string) => void;
  filterable?: boolean;
  filterInputPlaceholder?: string;
}

/**
 * Helper to find if parent navigation contains the active child branch
 * @param nav SideNavItems[] array of navigation branches to be checked
 * @param activeTab string currently active tab
 */
export const findParentBranches = (
  nav: SideNavItems[],
  activeTab: string
): boolean => {
  if (nav) {
    return (
      nav.some((navItem) => navItem.name === activeTab) ||
      nav.some((navItem) =>
        findParentBranches(navItem.subNavItems, activeTab)
      )
    );
  }
};

export const SpsSideNav: React.FC<SpsSideNavProps> = ({
                                                        title,
                                                        titleIcon,
                                                        navOptions,
                                                        onNavChange,
                                                        activeNavItem,
                                                        tabs,
                                                        onTabChange,
                                                        initialActiveTab,
                                                        filterable,
                                                        filterInputPlaceholder,
                                                      }) => {
  const previousActiveNavItem = useRef<string>(activeNavItem);
  const [activeBranch, setActiveBranch] = useState<string>(
    activeNavItem
  );
  const [activeTab, setActiveTab] = useState<string>(
    initialActiveTab || (tabs && tabs[0])
  );
  const [filterValue, setFilterValue] = useState<string>('');

  /**
   * Handler that sets selected branch as active
   * @param branch string navigation branch to be marked as active
   */
  const handleNavAction = (branch: string): void => {
    setActiveBranch(branch);
    if (onNavChange) {
      onNavChange(branch);
    }
  };

  /**
   * Handler that sets selected tab as active
   * @param tab string navigation branch to be marked as active
   */
  const handleTabAction = (tab: string): void => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  /**
   * Helper to filter top level navigation based on nested child branches
   * @param nav SideNavItems[] array of navigation branches to be checked
   */
  const filterChildBranches = (nav: SideNavItems[]): boolean => {
    if (nav) {
      return (
        nav.some((navItem) =>
          navItem.name.toLowerCase().includes(filterValue)
        ) ||
        nav.some((navItem) => filterChildBranches(navItem.subNavItems))
      );
    }
    return false;
  };

  /**
   * Filtered list of navigation options. If child of top level matches filter value,
   * the top level is kept.
   */
  const filteredNavOptions = navOptions.map(
    (option: SideNavSection): SideNavSection => {
      return {
        sectionTitle: option.sectionTitle,
        navItems: option.navItems.filter((navItem) => {
          return (
            navItem.name.toLowerCase().includes(filterValue) ||
            filterChildBranches(navItem.subNavItems)
          );
        }),
      };
    }
  );

  if (activeNavItem !== previousActiveNavItem.current) {
    setActiveBranch(activeNavItem);
    previousActiveNavItem.current = activeNavItem;
  }

  return (
      <div className="sps-side-nav sps-card">
        <div className="sps-card__header" title={title}>
          <div className="sps-card__title">
            {titleIcon && (
              <i className={`sps-icon sps-icon-${titleIcon}`} />
            )}
            {title}
          </div>
        </div>
        {tabs && (
          <nav className={'sps-nav sps-nav--tabs'}>
            {tabs.map((tab) => {
              return (
                <span
                  className={clsx(
                    'sps-nav__item',
                    'sps-nav__link',
                    tab === activeTab && 'active'
                  )}
                  data-toggle="tab"
                  role="tab"
                  tabIndex={0}
                  key={tab}
                  onClick={(): void => handleTabAction(tab)}
                  onKeyDown={(event): void => {
                    if (
                      event.key === 'Enter' ||
                      event.key === ' '
                    ) {
                      event.stopPropagation();
                      event.preventDefault();
                      handleTabAction(tab);
                    }
                  }}
                >
                                    <span>{tab}</span>
                                </span>
              );
            })}
          </nav>
        )}
        <div className="sps-card__body">
          {filterable && (
            <section className="sps-side-nav__section">
              <div className="sps-text-input">
                <div className="sps-form-control">
                  <i className="sps-text-input__icon sps-icon sps-icon-filter" />
                  <input
                    type="text"
                    className="sps-text-input__input"
                    placeholder={filterInputPlaceholder}
                    onChange={(event): void => {
                      setFilterValue(event.target.value);
                    }}
                  />
                </div>
              </div>
            </section>
          )}
          {filteredNavOptions.map((section: SideNavSection) => {
            return (
              <section
                className="sps-side-nav__section"
                key={section.sectionTitle}
              >
                <div
                  className="sps-side-nav__section-title"
                  style={{ fontWeight: 400 }}
                >
                  {section.sectionTitle}
                </div>
                {section.navItems.map((item) => {
                  return (
                    <SpsSideNavBranch
                      branch={{
                        ...item,
                        expanded: findParentBranches(
                          item.subNavItems,
                          activeNavItem
                        ),
                      }}
                      activeBranch={activeBranch}
                      setActiveBranch={(
                        branchName: string
                      ): void => {
                        handleNavAction(branchName);
                      }}
                      key={item.name}
                    />
                  );
                })}
              </section>
            );
          })}
        </div>
      </div>
  );
};
